import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/lib/shadcn/ui/collapsible";
import { SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from "@/lib/shadcn/ui/sidebar";
import { ChevronRight, LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/shadcn/utils";
import { Badge } from "@/lib/shadcn/ui/badge";
import { NavSubItem } from "@/lib/shadcn/components/sidebar/nav-platform";

export interface NavItemProps {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: NavSubItem[];
}

function hasBadge(subItem: NavSubItem): boolean {
    return subItem.badgeCount !== undefined && subItem.badgeCount > 0;
}

function badgeLabelOf(subItem: NavSubItem): string {
    return subItem.badgeLabel ?? "pending";
}

/**
 * The badge lives inside the link, so what a screen reader announces is the
 * LINK's accessible name — and name-from-content drops whitespace-only text
 * nodes, which ran the title into the count ("Sync issues3 wedged"). State the
 * name explicitly instead of hoping a separator survives (WCAG 4.1.2, 1.3.1).
 * Undefined when there is no badge: the title alone is already the right name.
 *
 * This aria-label is the ONLY announced form of the badge — an aria-label on
 * the link overrides everything inside it, so the badge carries the count alone
 * and no sr-only copy of the label (a second source of the same wording is a
 * drift hazard, not a redundancy).
 */
function subItemAccessibleName(subItem: NavSubItem): string | undefined {
    return hasBadge(subItem) ? `${subItem.title} ${subItem.badgeCount} ${badgeLabelOf(subItem)}` : undefined;
}

export const NavItem: React.FC<NavItemProps> = (item) => {
    const location = useLocation();
    const isCurrentPath = location.pathname === item.url;
    const hasActiveChild = item.items?.some((subItem) => location.pathname === subItem.url);

    const activeClassName = "bg-gray-700 text-white";
    const hoverClassName = "hover:bg-gray-700 hover:text-white";

    if (item.items) {
        return (
            <Collapsible key={item.title} asChild defaultOpen={hasActiveChild || item.isActive} className="group/collapsible">
                <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={item.title} className={cn(hoverClassName, hasActiveChild && activeClassName)}>
                            {item.icon && <item.icon />}
                            <span>{item.title}</span>
                            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <SidebarMenuSub>
                            {item.items?.map((subItem) => (
                                <SidebarMenuSubItem key={subItem.title}>
                                    <SidebarMenuSubButton
                                        asChild
                                        className={cn(hoverClassName, location.pathname === subItem.url && activeClassName)}
                                    >
                                        <Link to={subItem.url} aria-label={subItemAccessibleName(subItem)}>
                                            <span>{subItem.title}</span>
                                            {hasBadge(subItem) && (
                                                <Badge
                                                    data-testid={`nav-badge-${subItem.url}`}
                                                    variant="secondary"
                                                    className="ml-auto"
                                                >
                                                    {subItem.badgeCount}
                                                </Badge>
                                            )}
                                        </Link>
                                    </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                            ))}
                        </SidebarMenuSub>
                    </CollapsibleContent>
                </SidebarMenuItem>
            </Collapsible>
        );
    } else {
        return (
            <SidebarMenuButton asChild className={cn(hoverClassName, isCurrentPath && activeClassName)}>
                <Link to={item.url}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                </Link>
            </SidebarMenuButton>
        );
    }
};
